// Regras e cálculos dos valores e campos fiscais do CTe (ICMS, IBS/CBS e componentes do valor),

export const VALOR_MAXIMO_PRESTACAO = 9999999.99

// Matriz do MOC (G049-G052): 5xxx interna, 6xxx interestadual, 7xxx exterior;
// 5932/6932 somente quando a UF do emissor difere da UF de início da prestação
export function erroCfop(codigo, ufEmissor, ufInicio, ufFim) {
  const cfop = String(codigo || '').trim()
  if (!/^\d{4}$/.test(cfop)) return 'CFOP inválido'
  if (!ufInicio || !ufFim) return null

  const esperado = ufFim === 'EX' ? '7' : ufInicio === ufFim ? '5' : '6'
  if (cfop[0] !== esperado) {
    return `CFOP ${cfop} incompatível com a prestação (início ${ufInicio}, fim ${ufFim}): deve começar com ${esperado}`
  }
  if (ufFim !== 'EX' && ufEmissor) {
    const is932 = cfop === '5932' || cfop === '6932'
    if (ufEmissor !== ufInicio && !is932) {
      return `Emissor em ${ufEmissor} com prestação iniciando em ${ufInicio}: o CFOP deve ser 5932 ou 6932`
    }
    if (ufEmissor === ufInicio && is932) {
      return `CFOP ${cfop} só pode ser usado quando a UF do emissor é diferente da UF de início (${ufInicio})`
    }
  }
  return null
}

// Sem UFs definidas devolve tudo menos 7xxx (não há UF "EX" no cadastro)
export function filtrarCfops(cfops, ufEmissor, ufInicio, ufFim) {
  return (cfops || []).filter(cfop => {
    if (!ufInicio || !ufFim) return !String(cfop.codigo || '').startsWith('7')
    return erroCfop(cfop.codigo, ufEmissor, ufInicio, ufFim) === null
  })
}

// nOcc do leiaute da SEFAZ: 1 a 6 dígitos, sem zero à esquerda
export function erroNumeroOrdemColeta(valor) {
  if (valor === null || valor === undefined || String(valor).trim() === '') {
    return 'O campo Nº Ordem Coleta é obrigatório'
  }
  if (!/^[1-9]\d{0,5}$/.test(String(valor).trim())) {
    return 'O Nº Ordem Coleta deve ter de 1 a 6 dígitos, sem zero à esquerda (leiaute SEFAZ)'
  }
  return null
}

export const VALORES_FISCAIS_INICIAIS = {
  valor_total: null,
  valor_receber: null,
  icms_situacao_tributaria: null,
  icms_base_calculo: null,
  icms_aliquota: null,
  icms_valor: null,
  ibs_cbs_situacao_tributaria: null,
  ibs_cbs_classificacao_tributaria: null,
  ibs_cbs_base_calculo: null,
  ibs_uf_aliquota: null,
  ibs_uf_valor: null,
  ibs_mun_aliquota: null,
  ibs_mun_valor: null,
  ibs_valor_total: null,
  cbs_aliquota: null,
  cbs_valor: null,
}

export const ERROS_VALORES_FISCAIS = {
  valor_total: 'O campo Valor Bruto Frete é obrigatório',
  valor_receber: 'O campo Valor Líquido Frete é obrigatório',
  icms_situacao_tributaria: 'O campo Situação Tributária ICMS é obrigatório',
  icms_base_calculo: 'O campo Base de cálculo ICMS é obrigatório',
  icms_aliquota: 'O campo Alíquota ICMS é obrigatório',
  icms_valor: 'O campo Valor ICMS é obrigatório',
  ibs_cbs_situacao_tributaria: 'O campo Situação Tributária IBS/CBS é obrigatório',
  ibs_cbs_classificacao_tributaria: 'O campo Classificação Tributária IBS/CBS é obrigatório',
  ibs_cbs_base_calculo: 'O campo Base de cálculo IBS/CBS é obrigatório',
  ibs_uf_aliquota: 'O campo Alíquota IBS - UF é obrigatório',
  ibs_uf_valor: 'O campo Valor IBS - UF é obrigatório',
  ibs_mun_aliquota: 'O campo Alíquota IBS - Município é obrigatório',
  ibs_mun_valor: 'O campo Valor IBS - Município é obrigatório',
  ibs_valor_total: 'O campo Valor IBS - Total é obrigatório',
  cbs_aliquota: 'O campo Alíquota CBS é obrigatório',
  cbs_valor: 'O campo Valor CBS é obrigatório',
}

export function round2(v) {
  return Number(Math.round(v + 'e2') + 'e-2')
}

export function toNum(v) {
  const n = parseFloat(v)
  return isFinite(n) ? n : null
}

let proximaChave = 1

export function novoComponente(nome = '', valor = null) {
  return { key: proximaChave++, nome: String(nome || '').slice(0, 15), valor }
}

export const somaComponentes = comps =>
  round2(comps.reduce((soma, c) => soma + (toNum(c.valor) || 0), 0))

// Componentes prontos para o payload: só os preenchidos, nome até 15 caracteres e valor com 2 casas
export function montarComponentesValor(componentes) {
  return componentes
    .filter(c => c.nome && String(c.nome).trim() && toNum(c.valor) != null)
    .map(c => ({ nome: String(c.nome).trim().slice(0, 15), valor: round2(toNum(c.valor)) }))
}

const percentual = (base, aliquota) =>
  base != null && aliquota != null ? round2(base * (aliquota / 100)) : null

const soma = (a, b) => (a != null && b != null ? round2(a + b) : null)

const ouAnterior = (calculado, anterior) => (calculado != null ? calculado : anterior)

// Aplica um novo total do frete no state, recalculando bases e valores dependentes
export function aplicarTotal(prevState, total) {
  const ufValor = ouAnterior(
    percentual(total, toNum(prevState.ibs_uf_aliquota)),
    prevState.ibs_uf_valor,
  )
  const munValor = ouAnterior(
    percentual(total, toNum(prevState.ibs_mun_aliquota)),
    prevState.ibs_mun_valor,
  )
  return {
    ...prevState,
    valor_total: total,
    valor_receber: total,
    icms_base_calculo: total,
    icms_valor: ouAnterior(percentual(total, toNum(prevState.icms_aliquota)), prevState.icms_valor),
    ibs_cbs_base_calculo: total,
    ibs_uf_valor: ufValor,
    ibs_mun_valor: munValor,
    ibs_valor_total: ouAnterior(soma(toNum(ufValor), toNum(munValor)), prevState.ibs_valor_total),
    cbs_valor: ouAnterior(percentual(total, toNum(prevState.cbs_aliquota)), prevState.cbs_valor),
  }
}

// Altera um campo fiscal recalculando os dependentes (valor = base x alíquota; total do IBS = UF + município)
export function aplicarCampoFiscal(prevState, campo, value) {
  const next = { ...prevState, [campo]: value }
  const n = toNum(value)
  const baseIbsCbs = toNum(prevState.ibs_cbs_base_calculo)
  switch (campo) {
    case 'valor_receber':
      next.icms_base_calculo = value
      next.icms_valor = ouAnterior(
        percentual(n, toNum(prevState.icms_aliquota)),
        prevState.icms_valor,
      )
      break
    case 'icms_base_calculo':
      next.icms_valor = ouAnterior(
        percentual(n, toNum(prevState.icms_aliquota)),
        prevState.icms_valor,
      )
      break
    case 'icms_aliquota':
      next.icms_valor = ouAnterior(
        percentual(toNum(prevState.icms_base_calculo), n),
        prevState.icms_valor,
      )
      break
    case 'ibs_cbs_base_calculo': {
      const ufValor = ouAnterior(
        percentual(n, toNum(prevState.ibs_uf_aliquota)),
        prevState.ibs_uf_valor,
      )
      const munValor = ouAnterior(
        percentual(n, toNum(prevState.ibs_mun_aliquota)),
        prevState.ibs_mun_valor,
      )
      next.ibs_uf_valor = ufValor
      next.ibs_mun_valor = munValor
      next.ibs_valor_total = ouAnterior(
        soma(toNum(ufValor), toNum(munValor)),
        prevState.ibs_valor_total,
      )
      next.cbs_valor = ouAnterior(percentual(n, toNum(prevState.cbs_aliquota)), prevState.cbs_valor)
      break
    }
    case 'ibs_uf_aliquota': {
      const ufValor = ouAnterior(percentual(baseIbsCbs, n), prevState.ibs_uf_valor)
      next.ibs_uf_valor = ufValor
      next.ibs_valor_total = ouAnterior(
        soma(toNum(ufValor), toNum(prevState.ibs_mun_valor)),
        prevState.ibs_valor_total,
      )
      break
    }
    case 'ibs_uf_valor':
      next.ibs_valor_total = ouAnterior(
        soma(n, toNum(prevState.ibs_mun_valor)),
        prevState.ibs_valor_total,
      )
      break
    case 'ibs_mun_aliquota': {
      const munValor = ouAnterior(percentual(baseIbsCbs, n), prevState.ibs_mun_valor)
      next.ibs_mun_valor = munValor
      next.ibs_valor_total = ouAnterior(
        soma(toNum(prevState.ibs_uf_valor), toNum(munValor)),
        prevState.ibs_valor_total,
      )
      break
    }
    case 'ibs_mun_valor':
      next.ibs_valor_total = ouAnterior(
        soma(toNum(prevState.ibs_uf_valor), n),
        prevState.ibs_valor_total,
      )
      break
    case 'cbs_aliquota':
      next.cbs_valor = ouAnterior(percentual(baseIbsCbs, n), prevState.cbs_valor)
      break
    case 'ibs_cbs_situacao_tributaria':
      next.ibs_cbs_classificacao_tributaria = null
      break
    default:
      break
  }
  return next
}

// ICMS calculado "por dentro": o valor do frete informado ainda não embute o ICMS
export function calcularIcmsPorDentro(valorFrete, aliquota) {
  const frete = toNum(valorFrete)
  const aliq = toNum(aliquota)
  if (frete == null || aliq == null || aliq <= 0 || aliq >= 100) {
    return { valido: false, base: null, valor: null }
  }
  return {
    valido: true,
    base: round2(frete / (1 - aliq / 100)),
    valor: round2((frete * (aliq / 100)) / (1 - aliq / 100)),
  }
}

// Primeira inconsistência dos valores e campos fiscais (espelho das regras do backend), ou null
export function erroValoresFiscais(state) {
  for (const campo of Object.keys(ERROS_VALORES_FISCAIS)) {
    if (state[campo] === null || state[campo] === undefined || state[campo] === '') {
      return ERROS_VALORES_FISCAIS[campo]
    }
  }
  if (toNum(state.valor_receber) > toNum(state.valor_total)) {
    return 'O Valor Líquido do Frete deve ser menor ou igual ao Valor Bruto'
  }
  if (toNum(state.valor_total) > VALOR_MAXIMO_PRESTACAO) {
    return 'Valor da prestação acima do limite de R$ 9.999.999,99'
  }
  return null
}
